import React from 'react'
import { Field, useFormikContext } from 'formik'
import * as yup from 'yup'
import { DatePicker, InputField, RadioGroupField } from 'components'
import { Legend } from '../Legend'
import { Fieldset } from 'components/Layout/Fieldset'
import { FlexInputs } from 'components/Layout/FlexInputs'
import { FormDamageReport } from '../types'
import { useTranslation } from 'react-i18next'
import { formatHtmlDateInput } from 'utils/format'

export const validationSchema = yup.object({
  licensePlate: yup
    .string()
    .required(
      'pages.damageReports.damageReportForm.accidentFields.validation.licensePlateOfficial'
    ),
  date: yup
    .date()
    .required(
      'pages.damageReports.damageReportForm.accidentFields.validation.date'
    ),
  time: yup
    .string()
    .required(
      'pages.damageReports.damageReportForm.accidentFields.validation.time'
    )
    .matches(
      /\d\d[:.]?\d\d/,
      'pages.damageReports.damageReportForm.accidentFields.validation.timeMatch'
    ),
  street: yup
    .string()
    .required(
      'pages.damageReports.damageReportForm.accidentFields.validation.street'
    ),
  postcode: yup
    .string()
    .required(
      'pages.damageReports.damageReportForm.accidentFields.validation.postcode'
    ),
  city: yup
    .string()
    .required(
      'pages.damageReports.damageReportForm.accidentFields.validation.city'
    ),
  description: yup
    .string()
    .required(
      'pages.damageReports.damageReportForm.accidentFields.validation.description'
    ),
  hadPoliceReport: yup
    .boolean()
    .required(
      'pages.damageReports.damageReportForm.accidentFields.validation.option'
    ),
  policeReport: yup.string().when('hadPoliceReport', {
    is: true,
    then: yup
      .string()
      .required(
        'pages.damageReports.damageReportForm.accidentFields.validation.policeDepartment'
      ),
    otherwise: yup.string(),
  }),
  vehicleDrivable: yup
    .boolean()
    .required(
      'pages.damageReports.damageReportForm.accidentFields.validation.option'
    ),
  vehicleLocation: yup.object().when('vehicleDrivable', {
    is: false,
    then: yup.object({
      street: yup
        .string()
        .required(
          'pages.damageReports.damageReportForm.accidentFields.validation.street'
        ),
      postcode: yup
        .string()
        .required(
          'pages.damageReports.damageReportForm.accidentFields.validation.postcode'
        ),
      city: yup
        .string()
        .required(
          'pages.damageReports.damageReportForm.accidentFields.validation.city'
        ),
      description: yup.string(),
    }),
    otherwise: yup.object(),
  }),
})

export const AccidentFields: React.FunctionComponent = () => {
  const {
    values: { hadPoliceReport, vehicleDrivable },
  } = useFormikContext<FormDamageReport>()
  const { t } = useTranslation()
  const yesNoOptions = [
    { label: t('global.yes'), value: true },
    { label: t('global.no'), value: false },
  ]
  return (
    <>
      <Fieldset>
        <Legend>
          {t(
            'pages.damageReports.damageReportForm.accidentFields.licensePlate'
          )}
        </Legend>
        <Field
          component={InputField}
          type="text"
          id="licensePlate"
          name="licensePlate"
          label={t(
            'pages.damageReports.damageReportForm.accidentFields.licensePlate'
          )}
          required
        />
      </Fieldset>

      <Fieldset>
        <Legend>
          {t('pages.damageReports.damageReportForm.accidentFields.when')}
        </Legend>
        <FlexInputs>
          <Field
            component={DatePicker}
            type="date"
            id="date"
            name="date"
            label={t(
              'pages.damageReports.damageReportForm.accidentFields.dateField'
            )}
            placeholder="DD.MM.YYYY"
            pattern="\d{2}\.\d{2}\.\d{4}"
            max={formatHtmlDateInput(Date())}
            required
          />

          <Field
            component={InputField}
            type="time"
            id="time"
            name="time"
            label={t(
              'pages.damageReports.damageReportForm.accidentFields.timeField'
            )}
            pattern="\d\d:\d\d"
            required
          />
        </FlexInputs>
      </Fieldset>

      <Fieldset>
        <Legend>
          {t('pages.damageReports.damageReportForm.accidentFields.where')}
        </Legend>

        <FlexInputs>
          <Field
            component={InputField}
            type="text"
            id="street"
            name="street"
            label={t(
              'pages.damageReports.damageReportForm.accidentFields.streetField'
            )}
            required
          />
          <Field
            component={InputField}
            type="text"
            id="postcode"
            name="postcode"
            label={t(
              'pages.damageReports.damageReportForm.accidentFields.postcodeField'
            )}
            required
          />
          <Field
            component={InputField}
            type="text"
            id="city"
            name="city"
            label={t(
              'pages.damageReports.damageReportForm.accidentFields.cityField'
            )}
            required
          />
        </FlexInputs>
      </Fieldset>

      <Field
        component={InputField}
        textarea
        id="description"
        name="description"
        label={t(
          'pages.damageReports.damageReportForm.accidentFields.descriptionField'
        )}
        required
      />

      <Fieldset>
        <Legend>
          {t(
            'pages.damageReports.damageReportForm.accidentFields.readyToDrive'
          )}
        </Legend>

        <RadioGroupField
          name="vehicleDrivable"
          radios={yesNoOptions}
          inline
          required
        />

        {vehicleDrivable !== undefined && !vehicleDrivable && (
          <Fieldset>
            <Legend>
              {t(
                'pages.damageReports.damageReportForm.accidentFields.vehiclePlace'
              )}
            </Legend>

            <FlexInputs>
              <Field
                component={InputField}
                type="text"
                id="vehicleLocation-street"
                name="vehicleLocation.street"
                label={t(
                  'pages.damageReports.damageReportForm.accidentFields.streetField'
                )}
                required
              />
              <Field
                component={InputField}
                type="text"
                id="vehicleLocation-postcode"
                name="vehicleLocation.postcode"
                label={t(
                  'pages.damageReports.damageReportForm.accidentFields.postcodeField'
                )}
                required
              />
              <Field
                component={InputField}
                type="text"
                id="vehicleLocation-city"
                name="vehicleLocation.city"
                label={t(
                  'pages.damageReports.damageReportForm.accidentFields.cityField'
                )}
                required
              />
            </FlexInputs>

            <Field
              component={InputField}
              textarea
              id="vehicleLocation-description"
              name="vehicleLocation.description"
              label={t(
                'pages.damageReports.damageReportForm.accidentFields.commentField'
              )}
            />
          </Fieldset>
        )}
      </Fieldset>

      <Fieldset>
        <Legend>
          {t(
            'pages.damageReports.damageReportForm.accidentFields.contactPolice'
          )}
        </Legend>
        <RadioGroupField
          name="hadPoliceReport"
          radios={yesNoOptions}
          inline
          required
        />

        {hadPoliceReport && (
          <Field
            component={InputField}
            type="text"
            id="policeReport"
            name="policeReport"
            label={t(
              'pages.damageReports.damageReportForm.accidentFields.policeDepartmentField'
            )}
            required
          />
        )}
      </Fieldset>
    </>
  )
}
